/**
 * Barra de estadísticas: totales de vehículos y pasajeros
 * (todos y completados; en modal, ambos bloques apilados).
 */
import { Car, Bike, User, PersonStanding, Caravan, Truck, Bus, Baby } from 'lucide-react';
import styles from './StatsBar.module.css';

export interface FormTotals {
    vehiculos: number;
    motos: number;
    camiones: number;
    autobuses: number;
    adultos: number;
    niños: number;
    bebes: number;
    remolques: number;
}

function StatCell({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
    return (
        <div className={styles.cell}>
            {icon}
            <span className={styles.cellValue}>{value}</span>
            <span className={styles.cellLabel}>{label}</span>
        </div>
    );
}

function TotalsTwoColumns({ active, accent = 'default' }: { active: FormTotals; accent?: 'default' | 'success' }) {
    const totalPersonas = active.adultos + active.niños + active.bebes;
    const totalVehiculos = active.vehiculos + active.motos + active.camiones + active.autobuses + active.remolques;
    const iconColor = accent === 'success' ? 'currentColor' : '#002B45';
    const contentRowClass =
        accent === 'success' ? `${styles.contentRow} ${styles.contentRowSuccess}` : styles.contentRow;

    return (
        <div className={contentRowClass}>
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <div className={styles.badge}>
                        <span className={styles.badgeNumber}>{totalVehiculos}</span>
                    </div>
                    <span className={styles.sectionTitle}>Vehículos</span>
                </div>
                <div className={styles.cellRow}>
                    {active.vehiculos > 0 && <StatCell icon={<Car size={16} color={iconColor} />} value={active.vehiculos} label="Coche" />}
                    {active.motos > 0 && <StatCell icon={<Bike size={16} color={iconColor} />} value={active.motos} label="Moto" />}
                    {active.camiones > 0 && <StatCell icon={<Truck size={16} color={iconColor} />} value={active.camiones} label="Camión" />}
                    {active.autobuses > 0 && <StatCell icon={<Bus size={16} color={iconColor} />} value={active.autobuses} label="Autobús" />}
                    {active.remolques > 0 && <StatCell icon={<Caravan size={16} color={iconColor} />} value={active.remolques} label="Remolque" />}
                </div>
            </div>

            <div className={styles.midDivider} aria-hidden />

            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <div className={styles.badge}>
                        <span className={styles.badgeNumber}>{totalPersonas}</span>
                    </div>
                    <span className={styles.sectionTitle}>Personas</span>
                </div>
                <div className={styles.cellRow}>
                    {active.adultos > 0 && <StatCell icon={<User size={16} color={iconColor} />} value={active.adultos} label="Adulto" />}
                    {active.niños > 0 && <StatCell icon={<PersonStanding size={16} color={iconColor} />} value={active.niños} label="Niño" />}
                    {active.bebes > 0 && <StatCell icon={<Baby size={16} color={iconColor} />} value={active.bebes} label="Bebé" />}
                </div>
            </div>
        </div>
    );
}

interface StatsBarProps {
    totals: FormTotals;
    checkedTotals: FormTotals;
    /** En el modal: dos bloques (totales + completados) con la misma rejilla. */
    embedded?: boolean;
}

export default function StatsBar({ totals, checkedTotals, embedded = false }: StatsBarProps) {
    if (embedded) {
        return (
            <div className={`${styles.container} ${styles.containerEmbedded}`}>
                <div className={styles.modalStack}>
                    <div className={styles.statsBlock}>
                        <h3 className={styles.blockTitle}>Reservas</h3>
                        <TotalsTwoColumns active={totals} />
                    </div>
                    <div className={`${styles.statsBlock} ${styles.statsBlockCompleted}`}>
                        <h3 className={styles.blockTitle}>Reservas completadas</h3>
                        <TotalsTwoColumns active={checkedTotals} accent="success" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <TotalsTwoColumns active={totals} />
        </div>
    );
}
